import React, { useState, useEffect } from 'react';
import {
  Container, Row, Col, Card, Form, Button, Table,
  Modal, Badge, Alert, Spinner, Image
} from 'react-bootstrap';
import axios from 'axios';
import AdminNavbar from './components/AdminNavbar';
import { BASE_URL, UPLOAD_BASE_URL } from '../../services/api';

const SiteHeaderManager = () => {
  const [headers, setHeaders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [optionsLoading, setOptionsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [user, setUser] = useState(null);
  
  // Form states
  const [showModal, setShowModal] = useState(false);
  const [editingHeader, setEditingHeader] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  
  // Options
  const [availableIcons, setAvailableIcons] = useState([]);
  
  const [formData, setFormData] = useState({
    organization_name: '',
    college_name: '',
    tagline: '',
    logo_icon: 'fas fa-graduation-cap',
    logo_image: '',
    is_active: false
  });

  const API_BASE_URL = BASE_URL;
  
  useEffect(() => {
    // Fetch user data from localStorage or API
    const userData = JSON.parse(localStorage.getItem('user') || 'null');
    setUser(userData);
    
    fetchHeaders();
    fetchOptions();
  }, []);

  const resetForm = () => {
    setFormData({
      organization_name: '',
      college_name: '',
      tagline: '',
      logo_icon: 'fas fa-graduation-cap',
      logo_image: '',
      is_active: false
    });
    setLogoPreview(null);
  };

  const handleLogout = () => {
    localStorage.removeItem('user');
    localStorage.removeItem('token');
    window.location.href = '/admin/login';
  };

  const fetchHeaders = async () => {
    try {
      setLoading(true);
      setError('');
      const token = localStorage.getItem('token');
      
      const response = await axios.get(`${API_BASE_URL}/site_header.php?admin=true`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.data.success) {
        const processedHeaders = response.data.data.map(header => ({
          ...header,
          id: parseInt(header.id),
          is_active: header.is_active === "1" || header.is_active === 1
        }));
        
        setHeaders(processedHeaders);
      } else {
        setError('Failed to load site headers');
      }
    } catch (err) {
      setError('Error connecting to server');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchOptions = async () => {
    try {
      setOptionsLoading(true);
      const token = localStorage.getItem('token');
      
      const response = await axios.get(`${API_BASE_URL}/site_header.php?options=true`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.data.success) {
        setAvailableIcons(response.data.data.icons || []);
      }
    } catch (err) {
      console.error('Error fetching options:', err);
    } finally {
      setOptionsLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result);
      };
      reader.readAsDataURL(file);
      
      // Set file for upload
      setFormData(prev => ({
        ...prev,
        logo_file: file
      }));
    }
  };

  const removeLogo = () => {
    setLogoPreview(null);
    setFormData(prev => ({
      ...prev,
      logo_image: '',
      logo_file: null
    }));
  };

  const uploadLogo = async (file) => {
    try {
      setUploadingLogo(true);
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('logo', file);
      
      const response = await axios.post(`${API_BASE_URL}/upload_logo.php`, formData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      if (response.data.success) {
        return response.data.logo_url;
      }
      return null;
    } catch (err) {
      console.error('Logo upload error:', err);
      return null;
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      let dataToSend = { ...formData };
      
      // Remove logo_file from dataToSend if it exists
      if (dataToSend.logo_file) {
        delete dataToSend.logo_file;
      }
      
      // If there's a new logo to upload
      if (formData.logo_file) {
        const uploadedUrl = await uploadLogo(formData.logo_file);
        if (uploadedUrl) {
          dataToSend.logo_image = uploadedUrl;
        } else {
          setError('Failed to upload logo');
          return;
        }
      }
      
      const token = localStorage.getItem('token');
      let response;
      
      if (editingHeader) {
        response = await axios.put(
          `${API_BASE_URL}/site_header.php?id=${editingHeader.id}`,
          dataToSend,
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );
      } else {
        response = await axios.post(`${API_BASE_URL}/site_header.php`, dataToSend, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
      }
      
      if (response.data.success) {
        setSuccess(editingHeader ? 'Header updated!' : 'Header added!');
        setShowModal(false);
        resetForm();
        setEditingHeader(null);
        fetchHeaders();
        
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError('Operation failed: ' + (response.data.message || ''));
      }
    } catch (err) {
      setError('Error saving header: ' + (err.response?.data?.message || err.message));
      console.error(err);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this header? This will also delete the associated logo file.')) {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.delete(`${API_BASE_URL}/site_header.php?id=${id}`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (response.data.success) {
          setSuccess('Header deleted!');
          fetchHeaders();
          setTimeout(() => setSuccess(''), 3000);
        } else {
          setError('Failed to delete header: ' + (response.data.message || ''));
        }
      } catch (err) {
        setError('Error deleting header');
        console.error(err);
      }
    }
  };

  const handleEdit = (header) => {
    setEditingHeader(header);
    setFormData({
      organization_name: header.organization_name,
      college_name: header.college_name,
      tagline: header.tagline || '',
      logo_icon: header.logo_icon || 'fas fa-graduation-cap',
      logo_image: header.logo_image || '',
      is_active: header.is_active === true || header.is_active === "1" || header.is_active === 1
    });
    
    // Set logo preview if logo_image exists
    if (header.logo_image) {
      setLogoPreview(header.logo_image);
    }
    
    setShowModal(true);
  };

  const handleToggleActive = async (header) => {
    try {
      const currentActive = header.is_active === true || header.is_active === "1" || header.is_active === 1;
      const newActiveStatus = !currentActive;
      
      const updatedData = {
        organization_name: header.organization_name,
        college_name: header.college_name,
        tagline: header.tagline || '',
        logo_icon: header.logo_icon || 'fas fa-graduation-cap',
        logo_image: header.logo_image || '',
        is_active: newActiveStatus ? 1 : 0
      };
      
      const token = localStorage.getItem('token');
      const response = await axios.put(
        `${API_BASE_URL}/site_header.php?id=${header.id}`,
        updatedData,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );
      
      if (response.data.success) {
        fetchHeaders();
      } else {
        setError('Failed to update status: ' + (response.data.message || ''));
      }
    } catch (err) {
      console.error('Error toggling active status:', err);
      setError('Failed to update status');
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingHeader(null);
    resetForm();
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getLogoUrl = (logoPath) => {
    if (!logoPath) return null;
    return `${UPLOAD_BASE_URL}${logoPath}`;
  };

  if (loading) {
    return (
      <>
        <AdminNavbar onLogout={handleLogout} user={user} />
        <Container className="mt-5 text-center">
          <Spinner animation="border" variant="primary" />
          <p className="mt-3">Loading site headers...</p>
        </Container>
      </>
    );
  }

  return (
    <>
      <AdminNavbar onLogout={handleLogout} user={user} />
      <Container fluid className="py-4">
        <Row className="mb-4">
          <Col>
            <h2>Site Header Manager</h2>
            <p className="text-muted">Manage your website header and logo</p>
          </Col>
          <Col className="text-end">
            <Button variant="primary" onClick={() => setShowModal(true)}>
              <i className="fas fa-plus me-2"></i>Add Header
            </Button>
          </Col>
        </Row>

        {error && <Alert variant="danger" onClose={() => setError('')} dismissible>{error}</Alert>}
        {success && <Alert variant="success" onClose={() => setSuccess('')} dismissible>{success}</Alert>}

        <Card>
          <Card.Body>
            <Table responsive hover>
              <thead>
                <tr>
                  <th width="80">Logo</th>
                  <th>Organization</th>
                  <th>Site Title</th>
                  <th>Tagline</th>
                  <th>Status</th>
                  <th>Created</th>
                  <th>Updated</th>
                  <th width="150">Actions</th>
                </tr>
              </thead>
              <tbody>
                {headers.map((header) => (
                  <tr key={header.id}>
                    <td>
                      <div className="d-flex align-items-center">
                        {header.logo_image ? (
                          <Image 
                            src={getLogoUrl(header.logo_image)} 
                            alt="Logo"
                            rounded
                            style={{ width: '60px', height: '60px', objectFit: 'contain' }}
                            className="border"
                          />
                        ) : (
                          <div className="text-primary fs-4">
                            <i className={header.logo_icon}></i>
                          </div>
                        )}
                      </div>
                    </td>
                    <td>
                      <strong>{header.organization_name}</strong>
                    </td>
                    <td>
                      {header.college_name}
                    </td>
                    <td>
                      {header.tagline ? (
                        <small className="text-muted">{header.tagline}</small>
                      ) : (
                        <span className="text-muted">No tagline</span>
                      )}
                    </td>
                    <td>
                      <Form.Check
                        type="switch"
                        checked={header.is_active === true || header.is_active === "1" || header.is_active === 1}
                        onChange={() => handleToggleActive(header)}
                        label={(header.is_active === true || header.is_active === "1" || header.is_active === 1) ? 'Active' : 'Inactive'}
                        key={`active-${header.id}-${header.is_active}`}
                      />
                      {header.is_active && (
                        <small className="text-success d-block">
                          <i className="fas fa-check-circle me-1"></i>
                          Currently displayed
                        </small>
                      )}
                    </td>
                    <td>
                      <small className="text-muted">
                        {formatDate(header.created_at)}
                      </small>
                    </td>
                    <td>
                      <small className="text-muted">
                        {formatDate(header.updated_at)}
                      </small>
                    </td>
                    <td>
                      <Button
                        size="sm"
                        variant="outline-primary"
                        className="me-2"
                        onClick={() => handleEdit(header)}
                      >
                        <i className="fas fa-edit"></i>
                      </Button>
                      
                      <Button
                        size="sm"
                        variant="outline-danger"
                        onClick={() => handleDelete(header.id)}
                        disabled={header.is_active}
                      >
                        <i className="fas fa-trash"></i>
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
            
            {headers.length === 0 && (
              <div className="text-center py-5 text-muted">
                <i className="fas fa-university fa-3x mb-3"></i>
                <p>No site headers found. Add your first header!</p>
              </div>
            )}
          </Card.Body>
        </Card>

        {/* Add/Edit Header Modal */}
        <Modal show={showModal} onHide={handleCloseModal} size="lg">
          <Modal.Header closeButton>
            <Modal.Title>
              {editingHeader ? 'Edit Site Header' : 'Add Site Header'}
            </Modal.Title>
          </Modal.Header>
          <Form onSubmit={handleSubmit}>
            <Modal.Body>
              <Row>
                <Col md={8}>
                  <Form.Group className="mb-3">
                    <Form.Label>Organization Name </Form.Label>
                    <Form.Control
                      type="text"
                      name="organization_name"
                      value={formData.organization_name}
                      onChange={handleInputChange}
                      
                      placeholder="e.g., VTECH  Group"
                    />
                    <Form.Text className="text-muted">
                      Main organization or parent company name
                    </Form.Text>
                  </Form.Group>
                  
                  <Form.Group className="mb-3">
                    <Form.Label>Site Title *</Form.Label>
                    <Form.Control
                      type="text"
                      name="college_name"
                      value={formData.college_name}
                      onChange={handleInputChange}
                      required
                      placeholder="e.g., VTECH Institute of Technology"
                    />
                    <Form.Text className="text-muted">
                      Specific Firm Name or institute name
                    </Form.Text>
                  </Form.Group>
                  
                  <Form.Group className="mb-3">
                    <Form.Label>Tagline</Form.Label>
                    <Form.Control
                      type="text"
                      name="tagline"
                      value={formData.tagline}
                      onChange={handleInputChange}
                      placeholder="e.g., Excellence in Education & Innovation"
                    />
                    <Form.Text className="text-muted">
                      Short and catchy tagline (optional)
                    </Form.Text>
                  </Form.Group>
                </Col>
                
                <Col md={4}>
                  {/* Logo Upload Section */}
                  <Form.Group className="mb-3">
                    <Form.Label>Logo Upload</Form.Label>
                    <div className="border rounded p-3 text-center">
                      {(logoPreview || formData.logo_image) ? (
                        <div>
                          <Image 
                            src={logoPreview?.startsWith('data:') ? logoPreview : getLogoUrl(formData.logo_image)}
                            alt="Logo Preview" 
                            fluid
                            className="mb-3"
                            style={{ maxHeight: '100px', objectFit: 'contain' }}
                          />
                          
                          <div className="d-grid gap-2">
                            <Form.Control
                              type="file"
                              accept="image/*"
                              onChange={handleLogoChange}
                              disabled={uploadingLogo}
                            />
                            <Button
                              variant="outline-danger"
                              size="sm"
                              onClick={removeLogo}
                            >
                              <i className="fas fa-trash me-1"></i> Remove Logo
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <div>
                          <div className="mb-3">
                            <i className="fas fa-image fa-3x text-muted"></i>
                          </div>
                          <Form.Control
                            type="file"
                            accept="image/*"
                            onChange={handleLogoChange}
                            disabled={uploadingLogo}
                          />
                          <Form.Text className="text-muted">
                            Upload institution logo (recommended: 200x200px, PNG/JPEG/SVG)
                          </Form.Text>
                        </div>
                      )}
                    </div>
                  </Form.Group>
                </Col>
              </Row>
              
              <Row>
                <Col md={8}>
                  <Form.Group className="mb-3">
                    <Form.Label>Fallback Icon</Form.Label>
                    {optionsLoading ? (
                      <Spinner size="sm" animation="border" />
                    ) : (
                      <Form.Select
                        name="logo_icon"
                        value={formData.logo_icon}
                        onChange={handleInputChange}
                      >
                        {availableIcons.map((icon, index) => (
                          <option key={index} value={icon}>
                            <i className={`${icon} me-2`}></i>
                            {icon.replace('fas fa-', '').replace('-', ' ')}
                          </option>
                        ))}
                      </Form.Select>
                    )}
                    <Form.Text className="text-muted">
                      FontAwesome icon used if no logo is uploaded
                    </Form.Text>
                  </Form.Group>
                </Col>
                
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Status</Form.Label>
                    <div>
                      <Form.Check
                        type="switch"
                        name="is_active"
                        label="Active"
                        checked={formData.is_active}
                        onChange={handleInputChange}
                      />
                    </div>
                    <Form.Text className="text-muted">
                      Only one header can be active at a time
                    </Form.Text>
                  </Form.Group>
                </Col>
              </Row>
              
              {/* Preview Section */}
              <Card className="border-secondary mt-3">
                <Card.Header>
                  <h6 className="mb-0">Preview</h6>
                </Card.Header>
                <Card.Body>
                  <div className="d-flex align-items-center">
                    <div className="me-3">
                      {(logoPreview || formData.logo_image) ? (
                        <Image 
                          src={logoPreview?.startsWith('data:') ? logoPreview : getLogoUrl(formData.logo_image)}
                          alt="Logo Preview" 
                          style={{ width: '80px', height: '80px', objectFit: 'contain' }}
                          className="border rounded"
                        />
                      ) : (
                        <div className="text-primary fs-1">
                          <i className={formData.logo_icon}></i>
                        </div>
                      )}
                    </div>
                    <div>
                      <div className="text-muted small">
                        {formData.organization_name || 'Organization Name'}
                      </div>
                      <h4 className="mb-1">
                        {formData.college_name || 'Site Title'}
                      </h4>
                      {formData.tagline && (
                        <div className="text-primary fst-italic">
                          {formData.tagline}
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <div className="mt-3 small text-muted">
                    <div className="d-flex">
                      <div className="me-4">
                        <i className="fas fa-circle me-1"></i>
                        Status: 
                        <span className={`ms-2 ${formData.is_active ? 'text-success' : 'text-warning'}`}>
                          {formData.is_active ? 'Active (Will be displayed)' : 'Inactive'}
                        </span>
                      </div>
                      <div>
                        <i className="fas fa-info-circle me-1"></i>
                        {formData.logo_image ? 'Custom Logo' : `Fallback Icon: ${formData.logo_icon}`}
                      </div>
                    </div>
                  </div>
                </Card.Body>
              </Card>
              
              {editingHeader && (
                <div className="alert alert-secondary mt-3">
                  <small>
                    <div className="row">
                      <div className="col-md-6">
                        <i className="fas fa-calendar-plus me-2"></i>
                        Created: {formatDate(editingHeader.created_at)}
                      </div>
                      <div className="col-md-6">
                        <i className="fas fa-sync-alt me-2"></i>
                        Last Updated: {formatDate(editingHeader.updated_at)}
                      </div>
                    </div>
                  </small>
                </div>
              )}
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={handleCloseModal}>
                Cancel
              </Button>
              <Button variant="primary" type="submit" disabled={uploadingLogo}>
                {uploadingLogo && <Spinner animation="border" size="sm" className="me-2" />}
                {editingHeader ? 'Update' : 'Add'} Header
              </Button>
            </Modal.Footer>
          </Form>
        </Modal>
      </Container>
    </>
  );
};

export default SiteHeaderManager;